import React, { FC, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { Button } from 'transfix-ui/components/Button'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { StrategyManagerModal } from 'apps/marketplace/components/rfp/StrategyManager/StrategyManagerModal'
import { Tabs, Tab, Toast, useToastState } from 'transfix-ui'
import { generatePageTitle } from 'utils/generateAppDefaults'
import { useModal } from 'utils/hooks'
import { ControlPanelTab } from './ControlPanelTab'
import { NormFileModal } from './NormFileModal'
import { NormingJobsTab } from './NormingJobsTab'
import { RefreshNormFileModal } from './RefreshNormFileModal'
import { RequestForProposalsTab } from './RequestForProposalsTab'
import { StoreAwardModal } from './StoreAwardModal'
import { StorePricesModal } from './StorePricesModal'

enum TABS {
  normingJobs = 'Norming Jobs',
  rfps = 'RFPs Stored in Database',
  controlPanel = 'Control Panel',
}

const InstantRfp: FC = () => {
  const stormStrategyManagement = useFeatureFlag('stormStrategyManagement')
  const [currentTab, setCurrentTab] = useState<TABS>(TABS.normingJobs)
  const changeCurrentTab = (newTab: TABS) => setCurrentTab(newTab)
  const { closeModal, isOpen, openModal } = useModal()
  const [
    storePricesModal,
    storeAwardModal,
    normFileModal,
    refreshNormFileModal,
    strategyManagerModal,
  ] = [
    'storePricesModal',
    'storeAwardModal',
    'normFileModal',
    'refreshNormFileModal',
    'strategyManagerModal',
  ]
  const { closeToast, multiLine, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const onModalSuccess = (message: string) =>
    openToast({
      toastType: 'default',
      toastMessage: message,
      multiLine: message.length > 300,
    })
  const onModalError = (error: string) =>
    openToast({
      toastType: 'alert',
      toastMessage: error,
      multiLine: error.length > 300,
    })
  const showToastMessage = (message: string) => {
    openToast({
      toastType: 'default',
      toastMessage: message,
    })
  }

  return (
    <DocumentTitle title={generatePageTitle('Instant RFP')}>
      <Box p={4}>
        <Box mb={2} display='flex' alignItems='center'>
          <Box flexGrow={1}>
            <Typography variant='h2'>Instant RFP</Typography>
          </Box>
          <Box>
            <Button
              onClick={openModal(storePricesModal)}
              color='secondary'
              id='store-data'
              data-test='store-data'
              size='large'>
              Store Prices
            </Button>
          </Box>
          <Box ml={1}>
            <Button
              onClick={openModal(storeAwardModal)}
              color='secondary'
              id='store-data'
              data-test='store-data'
              size='large'>
              Store Award
            </Button>
          </Box>
          <Box ml={1}>
            <Button
              onClick={openModal(refreshNormFileModal)}
              color='secondary'
              id='refresh-norm-file'
              data-test='refresh-norm-file'
              size='large'>
              Refresh Norm
            </Button>
          </Box>
          <Box ml={1}>
            <Button
              onClick={openModal(normFileModal)}
              color='primary'
              id='generate-norm-file'
              data-test='generate-norm-file'
              size='large'>
              Generate Norm File
            </Button>
          </Box>
        </Box>
        <Tabs value={currentTab} onChange={changeCurrentTab}>
          <Tab label={TABS.normingJobs}>
            <NormingJobsTab />
          </Tab>
          <Tab label={TABS.rfps}>
            <RequestForProposalsTab showToast={showToastMessage} />
          </Tab>
          {stormStrategyManagement && (
            <Tab label={TABS.controlPanel}>
              <ControlPanelTab onOpenStrategyManagerModal={openModal(strategyManagerModal)} />
            </Tab>
          )}
        </Tabs>
        {isOpen(storePricesModal) && (
          <StorePricesModal
            onSuccess={onModalSuccess}
            onError={onModalError}
            onClose={closeModal}
          />
        )}
        {isOpen(storeAwardModal) && (
          <StoreAwardModal onSuccess={onModalSuccess} onError={onModalError} onClose={closeModal} />
        )}
        {isOpen(refreshNormFileModal) && (
          <RefreshNormFileModal
            onSuccess={onModalSuccess}
            onError={onModalError}
            onClose={closeModal}
          />
        )}
        {isOpen(normFileModal) && (
          <NormFileModal onSuccess={onModalSuccess} onError={onModalError} onClose={closeModal} />
        )}
        {isOpen(strategyManagerModal) && <StrategyManagerModal onClose={closeModal} />}
        <Toast
          variation={toastType}
          open={toastOpen}
          onClose={closeToast}
          message={toastMessage}
          multiLine={multiLine}
        />
      </Box>
    </DocumentTitle>
  )
}

export default InstantRfp
